/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, LogoMediumIcon } from 'shared/components/Icons';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import ActionButtons from 'shared/components/likiee/ActionButtons';
import VisualSearchButton from 'shared/components/Credits/VisualSearchButton';
import StoreFilter from './StoreFilter';
import { sendEvent } from 'utils/analyticsHelpers';
import { getUserLocale, showPrice } from 'utils/appHelpers';

const getCheapestFromCard = card => {
  if (!card?.matches?.length) return null;

  // Filter out matches without valid prices
  const matchesWithPrices = card.matches.filter(match => {
    const price = match.price?.extracted_value || match.price?.value;
    return price && price > 0;
  });

  if (matchesWithPrices.length === 0) return null;

  return matchesWithPrices.reduce((cheapest, match) => {
    const matchPrice = match.price?.extracted_value || match.price?.value;
    const cheapestPrice = cheapest.price?.extracted_value || cheapest.price?.value;

    return matchPrice < cheapestPrice ? match : cheapest;
  }, matchesWithPrices[0]);
};

const createCheapestLookCard = cards => {
  const cheapestItems = cards.map(card => getCheapestFromCard(card)).filter(item => item !== null);

  if (cheapestItems.length === 0) return null;

  // Calculate total price
  const totalPrice = cheapestItems.reduce((sum, item) => {
    const price = item.price?.extracted_value || item.price?.value || 0;
    return sum + price;
  }, 0);

  // Create a synthetic price object for the total
  const totalPriceObj = {
    extracted_value: totalPrice,
    currency: cheapestItems[0]?.price?.currency || 'USD',
  };

  return {
    searchCategory: 'Cheapest Look',
    searchImage: 'cheapest-look-icon', // Custom minimal icon identifier
    matches: cheapestItems,
    isCheapestLook: true,
    totalPrice: totalPriceObj,
    searchType: 'cheapest-look',
  };
};
const getCardsFromData = (data, locale) => {
  if (!data?.length) {
    return [];
  }
  const localeData = data.filter(item => item.locale === locale);
  const finalData = localeData.length > 0 ? localeData : data;
  return finalData;
};
const ProductOtherOffers = ({ product, data, onClickSave }) => {
  const blockEl = useRef();
  const timeout = useRef();
  const scrollLeft = useRef();
  const { t } = useTranslation();
  const locale = getUserLocale();
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [btnState, setBtnState] = useState({
    leftDisabled: true,
    rightDisabled: false,
  });
  const [globalStoreFilter, setGlobalStoreFilter] = useState(new Set());

  const onScroll = e => {
    clearTimeout(timeout.current);
    scrollLeft.current = e.target.scrollLeft;
    timeout.current = setTimeout(() => {
      const btnState = {
        leftDisabled: e.target.scrollLeft === 0,
        rightDisabled: e.target.scrollLeft + e.target.clientWidth + 10 >= e.target.scrollWidth,
      };
      setBtnState({ ...btnState });
    }, 300);
  };

  // All useEffect hooks must be called before any early returns
  useEffect(() => {
    blockEl.current?.addEventListener('scroll', onScroll);
    return () => blockEl.current?.removeEventListener('scroll', onScroll);
  }, []);

  // Helper function to extract domain name (same as in StoreFilter)
  const extractDomainName = source => {
    if (!source) return 'Unknown';

    try {
      let domain = source;
      if (domain.includes('://')) {
        domain = domain.split('://')[1];
      }
      domain = domain.replace(/^www\./, '');
      domain = domain.split('/')[0];
      domain = domain.split(':')[0];
      return domain.charAt(0).toUpperCase() + domain.slice(1);
    } catch (error) {
      return source || 'Unknown';
    }
  };

  // Calculate finalCards before using it in useEffect
  const cards = data?.length ? getCardsFromData(data, locale) : [];
  let finalCards = [...cards];
  if (cards.length > 1) {
    const cheapestLookCard = createCheapestLookCard(cards);
    if (cheapestLookCard) {
      finalCards = [cheapestLookCard, ...cards];
    }
  }

  // Initialize global store filter when data changes
  useEffect(() => {
    if (finalCards.length > 0) {
      // Extract all unique stores from all cards
      const allStores = new Set();
      finalCards.forEach(card => {
        card.matches?.forEach(match => {
          if (match.source) {
            const domain = extractDomainName(match.source);
            allStores.add(domain);
          }
        });
      });
      setGlobalStoreFilter(allStores);
    }
  }, [data, locale]);

  if (product.isVisualMatchesInProgress) {
    return (
      <div className='mt-12 mt-md-40 px-8 px-md-0 w-100 pb-30'>
        <SkeletonLoader type='other-offers' count={4} />
      </div>
    );
  }

  // Show empty section with Visual Search button even when there are no visual matches
  if (!data?.length) {
    return (
      <div className='mt-12 mt-md-40 px-8 px-md-0 mw-100'>
        <div className='mb-10 mb-md-16 offer-slider-header product-slider offer-slider'>
          <div className='offer-title-section flex-shrink-1'>
            <div className='d-flex align-items-start justify-content-between w-100'>
              <div className='title-and-debug d-flex align-items-center flex-wrap'>
                <h1 className='mb-0 d-inline mr-8'>{t('title_other_offers')}</h1>
              </div>
              {/* Find Similar Button - always show to allow users to start visual search */}
              <VisualSearchButton
                likieeId={product.id}
                likiee={product}
                className='find-similar-button flex-shrink-0'
                isVisualMatchesInProgress={product.isVisualMatchesInProgress}
                showAsStartNew={true} // Always show as "Start New Search" when no matches
                showCreditCost={true}
                onSuccess={() => {
                  // Optionally handle success
                }}
              />
            </div>
          </div>
        </div>
        {/* Empty state message */}
        <div className='text-center py-5'>
          <p className='text-muted mb-3'>{t('no_visual_matches_yet', 'No similar products found yet')}</p>
          <p className='text-muted small'>
            {t('click_find_similar_to_start', 'Click "Start New Search" to find similar products')}
          </p>
        </div>
      </div>
    );
  }

  const showTabs = finalCards.length > 1;
  const selectedCard = finalCards[selectedCardIndex] || finalCards[0];
  const matches = selectedCard?.matches || [];

  // Calculate total matches across all cards for filter visibility
  const totalMatches = finalCards.reduce((total, card) => total + (card.matches?.length || 0), 0);
  const showStoreFilter = totalMatches > 4;

  // Apply global store filter to current card matches
  const displayMatches =
    globalStoreFilter.size === 0 || !showStoreFilter
      ? matches
      : matches.filter(match => {
          if (!match.source) return false;
          const domain = extractDomainName(match.source);
          return globalStoreFilter.has(domain);
        });

  // Get all matches from all cards for the store filter component
  const allMatches = finalCards.reduce((acc, card) => [...acc, ...(card.matches || [])], []);

  const handleStoreFilterChange = selectedStores => {
    setGlobalStoreFilter(selectedStores);
  };

  const onProductClick = match => {
    window.open(match.link, '_blank');
    const isAffilate = match.link !== match.original_url;
    sendEvent('product_other_offer', {
      parentProductId: product.id,
      isAffilate,
      source: match.source,
      originalUrl: match.original_url,
    });
  };
  const handleArrowClick = isRight => {
    const elLeft = scrollLeft.current || blockEl.current.scrollLeft;
    const left = elLeft + (isRight ? blockEl.current.clientWidth : 0 - blockEl.current.clientWidth);
    blockEl.current.scrollTo({ top: 0, left, behavior: 'smooth' });
    scrollLeft.current = left;
  };
  const debugInfo = showTabs
    ? `(${cards.length} cards, ${displayMatches.length} matches in selected card)`
    : `(${displayMatches.length} matches)`;
  return (
    <div className='mt-12 mt-md-40 px-8 px-md-0 mw-100'>
      <div className='mb-10 mb-md-16 offer-slider-header product-slider offer-slider'>
        <div className='offer-title-section flex-shrink-1'>
          <div className='d-flex align-items-start justify-content-between w-100'>
            <div className='title-and-debug d-flex align-items-center flex-wrap'>
              <h1 className='mb-0 d-inline mr-8'>{t('title_other_offers')}</h1>
              <small style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>{debugInfo}</small>
              {/* Store Filter */}
              {showStoreFilter && (
                <div className='ml-12 position-relative d-flex align-items-center'>
                  <StoreFilter matches={allMatches} onFilterChange={handleStoreFilterChange} />
                </div>
              )}
            </div>
            {/* Find Similar Button - positioned inline with title */}
            {product.isVisualMatchesInProgress !== true && (
              <VisualSearchButton
                likieeId={product.id}
                likiee={product} // Pass full product object for fashion logic
                className='find-similar-button flex-shrink-0'
                isVisualMatchesInProgress={product.isVisualMatchesInProgress}
                showAsStartNew={product.isVisualMatchesInProgress !== true}
                showCreditCost={true}
                onSuccess={() => {
                  // Optionally handle success
                }}
              />
            )}
          </div>
        </div>
        <div className='d-flex align-items-center'>
          {/* Navigation arrows */}
          <div className='align-items-center d-none d-md-flex slick-arrows'>
            <button
              className='btn p-0 btn-prev'
              disabled={btnState.leftDisabled}
              onClick={handleArrowClick.bind(null, false)}
            >
              <ArrowLeftIcon />
            </button>
            <button
              className='btn p-0 ml-12 btn-next'
              disabled={btnState.rightDisabled}
              onClick={handleArrowClick.bind(null, true)}
            >
              <ArrowLeftIcon />
            </button>
          </div>
        </div>
      </div>

      {/* Cards/Tabs Section */}
      {showTabs && (
        <div className='mb-16 d-flex overflow-auto pb-2' style={{ gap: '12px', position: 'relative' }}>
          {finalCards.map((card, index) => (
            <div
              key={index}
              className={`card-tab cursor-pointer p-8 border rounded d-flex flex-column align-items-center ${
                selectedCardIndex === index ? 'border-primary bg-light' : 'border-light'
              }`}
              style={{
                minWidth: '120px',
                textAlign: 'center',
                position: 'relative',
                backgroundColor: selectedCardIndex === index ? '#fff' : '#f8f9fa',
              }}
              onClick={() => setSelectedCardIndex(index)}
            >
              {card.searchImage && (
                <div className='mb-2'>
                  {card.searchImage.startsWith('http') ? (
                    <img
                      src={card.searchImage}
                      alt={card.searchCategory || 'Search'}
                      style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                  ) : card.searchImage === 'cheapest-look-icon' ? (
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        border: '1px solid #dee2e6',
                        position: 'relative',
                      }}
                    >
                      {/* Minimalistic price tag icon */}
                      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#6c757d' strokeWidth='1.5'>
                        <path d='M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z' />
                        <line x1='7' y1='7' x2='7.01' y2='7' />
                      </svg>
                    </div>
                  ) : (
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        fontSize: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        background: '#f8f9fa',
                      }}
                    >
                      {card.searchImage}
                    </div>
                  )}
                </div>
              )}
              <small className='fw-500 text-center' style={{ fontSize: '12px' }}>
                {card.searchCategory || `Card ${index + 1}`}
              </small>
              <small style={{ color: '#666', fontSize: '10px' }}>
                {card.isCheapestLook
                  ? `Total: ${showPrice(card.totalPrice, 'extracted_value')}`
                  : `${card.matches?.length || 0} items`}
              </small>
            </div>
          ))}
        </div>
      )}

      <div className='d-flex mw-100 overflow-auto other-offers-block pb-5 mpb-md-20' ref={blockEl}>
        {displayMatches?.map((match, i) => {
          const productUrl = match.original_url || match.link;
          const isLast = displayMatches.length - 1 === i;
          const actionButtons = [{ logo: <LogoMediumIcon />, onClick: () => onClickSave(productUrl) }];
          return (
            <div key={i} className={`product-item-block offer-item ${isLast ? '' : 'mr-8 mr-md-16'} cursor-pointer`}>
              <div
                className={`image-block d-flex flex-column justify-content-center mb-8`}
                onClick={onProductClick.bind(null, match)}
              >
                {match.source_icon && (
                  <div className='item-logo'>
                    <img src={match.source_icon} alt='limg' />
                  </div>
                )}
                <img src={match.thumbnail} alt='rimg' />
                {actionButtons && <ActionButtons buttons={actionButtons} />}
              </div>
              <div className='project-info mt-5'>
                <p className='mb-5 text-line-2 fw-500'>{match.title}</p>
                <div className='d-flex align-items-center justify-content-between'>
                  <p className='mb-0 text-lg'>{showPrice(match.price, 'extracted_value')}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default ProductOtherOffers;
